import React, { useEffect, useState } from 'react';
import { Input, message } from 'antd';
import { AimOutlined } from '@ant-design/icons';
import { IRackDetail } from '../../../../redux/api/inventory';

interface Props {
	rack: IRackDetail;
	highlightCode?: string;
	onHighlight: (code: string | undefined) => void;
	/** Scroll container holding the rack diagram (where slots live). */
	gridRef: React.RefObject<HTMLDivElement>;
}

/**
 * Quick-navigation strip for a rack: find a slot by code (e.g. "A5"), scroll it
 * into view and highlight it via `onHighlight`.
 */
const RackNavBar: React.FC<Props> = ({ rack, highlightCode, onHighlight, gridRef }) => {
	const [term, setTerm] = useState(highlightCode ?? '');

	// Keep the input in sync when the highlight is cleared elsewhere (e.g. when
	// switching racks).
	useEffect(() => {
		setTerm(highlightCode ?? '');
	}, [highlightCode]);

	const findSlot = (raw: string) => {
		const code = raw.trim().toUpperCase();
		if (!code) {
			onHighlight(undefined);
			return;
		}
		onHighlight(code);
		const el = gridRef.current?.querySelector(
			`[data-slot-code="${code}"]`,
		) as HTMLElement | null;
		if (el) {
			el.scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'center' });
		} else {
			message.info(`No location "${code}" in this rack`);
		}
	};

	const hasLocations = rack.shelves.some((s) => s.locations.length > 0);

	if (!hasLocations) return null;

	return (
		<div style={{ display: 'flex', marginTop: 10 }}>
			<Input.Search
				allowClear
				size="small"
				prefix={<AimOutlined style={{ color: '#bfbfbf' }} />}
				placeholder="Find a slot (e.g. A5)"
				value={term}
				onChange={(e) => {
					setTerm(e.target.value);
					if (!e.target.value) onHighlight(undefined);
				}}
				onSearch={findSlot}
				style={{ width: 220, marginLeft: 'auto' }}
			/>
		</div>
	);
};

export default RackNavBar;
